import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Button, Flexbox, MultiDropdown, WrapperBox } from "../../Theme/GlobalStyles";
import { globalTheme } from "../../Theme/Theme";
import { Text } from "../../Theme/Typography";
import { Clock } from "../../Theme/Icons";
import CustomDropdown from "../../../Components/CustomDropdown/CustomDropdown";
import { getAllCoworkUsers, coworkData } from "../../../redux/cowork/coworkSlice";
import { PopupWrapper } from "./CommonPopup";
import { SCHOOL_OPTIONS } from "../../Constants/schools";

// The shared MultiDropdown styling hides each chip's remove icon
// (`.chip img { display:none }`), so individual participants can't be removed.
// Re-enable it here (higher specificity) so clicking a chip's × removes just it.
const ParticipantsField = styled.div`
  .searchWrapper .chip {
    padding-right: 5px !important;
  }
  .searchWrapper .chip img {
    display: inline-block !important;
    width: 13px;
    height: 13px;
    margin-left: 6px;
    cursor: pointer;
    opacity: 0.65;
  }
  .searchWrapper .chip img:hover {
    opacity: 1;
  }
`;

// Same school options the student room-card booking form offers — shared with
// the calendar's school colouring so both agree on the list and the ids.

// 12h label that supports quarter hours (e.g. 13.25 → "1:15 PM").
const pad2 = (n) => String(n).padStart(2, "0");
const fmt12 = (h) => {
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    // 24 stands for midnight (end-of-day boundary), which is AM, not PM.
    const ampm = hh % 24 >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${pad2(mm)} ${ampm}`;
};
const fmtDuration = (start, end) => {
    const mins = Math.round((end - start) * 60);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h} hour${h === 1 ? "" : "s"}`;
    return `${m} min`;
};
const fmtPopupDate = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
};

const FREQUENCIES = [
    { label: "Does not repeat", value: "none" },
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
];

// 15-minute steps from 07:00 to 24:00 (midnight) for the editable start/end selects.
const TIME_VALUES = [];
for (let h = 7; h <= 24; h += 0.25) TIME_VALUES.push(Number(h.toFixed(2)));

const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${globalTheme.colors.nuetral2}`,
    fontSize: 13,
    fontFamily: "inherit",
    color: globalTheme.colors.black3,
    boxSizing: "border-box",
    outline: "none",
};

// Attendees reach this form in two different shapes: the `{id, name, value,
// label}` objects the create payload round-trips, and the saved join rows the
// calendar API returns (`{id, attendee_id}` — where `id` is the join row, NOT
// the user, so `attendee_id` has to win). Both become the `{cat, key}` pairs
// MultiDropdown works in.
//
// The saved rows carry no name, so the label is looked up in the same options
// list the picker is built from — the form already loads every cowork user.
const toChips = (attendees = [], options = []) => {
    const nameById = new Map((options || []).map((o) => [String(o.cat), o.key]));
    return (attendees || [])
        .map((a) => {
            const cat = a?.cat ?? a?.attendee_id ?? a?.value ?? a?.id ?? a?.user_id ?? null;
            const label = (
                a?.key ??
                a?.name ??
                a?.label ??
                `${a?.firstName || ""} ${a?.lastName || ""}`
            )
                .toString()
                .trim();
            return { cat, key: label || nameById.get(String(cat)) || "Participant" };
        })
        .filter((a) => a.cat != null);
};

// Admin "create booking" form shown after a slide-selection on the calendar.
// Collects a meeting title, optional attendees, instructor flag and an optional
// recurrence rule, then hands a normalised payload back to the calendar which
// dispatches createClassroomRequest (once per occurrence).
//
// The SAME form doubles as the edit form for an existing request
// (`mode="edit"`): the fields are prefilled from the request and the payload it
// hands back is identical, so the caller just routes it to the update thunk
// instead of the create one. `selection.wasApproved` flags an approved booking
// (My Bookings), which the save sends back for review.
// `showEduspaceFields={false}` hides (and stops requiring) the School and
// Event fields, for requests from modules that don't use them.
const CreateBookingPopup = ({ open, selection, onClose, onConfirm, loading = false, recurOnly = false, mode = "create", showEduspaceFields = true }) => {
    const dispatch = useDispatch();
    const { coworkUsers } = useSelector(coworkData);

    const [title, setTitle] = useState("");
    const [participants, setParticipants] = useState([]); // [{ cat, key }]
    const [school, setSchool] = useState("");
    const [needInstructor, setNeedInstructor] = useState(false);
    // "Is this booking related to an event?" — persisted as the isEvent boolean on
    // the booking / booking request.
    const [isEvent, setIsEvent] = useState(false);
    const [frequency, setFrequency] = useState("none");
    const [count, setCount] = useState(3);
    const [startH, setStartH] = useState(9);
    const [endH, setEndH] = useState(10);
    const [error, setError] = useState("");

    // A multi-day selection (slide across days in Week view) already carries its
    // dates, so the recurrence control is hidden for it.
    const multiDate = !!(selection?.dates && selection.dates.length);
    // Editing an existing request: same fields, prefilled, and never recurring —
    // one request is being changed, not a series created.
    const isEdit = mode === "edit" && !recurOnly;

    // Participant options — same source/shape as the student room-card form.
    const participantOptions = useMemo(
        () =>
            (coworkUsers || []).map((user) => {
                const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
                const truncated =
                    fullName.length > 20
                        ? fullName.slice(0, 20).split(" ").slice(0, -1).join(" ") + "..."
                        : fullName;
                return { cat: user.id, key: truncated };
            }),
        [coworkUsers]
    );

    // Load the participant list the first time the create form is opened.
    useEffect(() => {
        if (open && !recurOnly && !(coworkUsers && coworkUsers.length)) {
            dispatch(getAllCoworkUsers({ isLabStudent: false }));
        }
    }, [open, recurOnly, coworkUsers, dispatch]);

    // The user list arrives asynchronously, so an edit opened before it landed
    // shows placeholder chip labels. Re-label them in place once the options are
    // here — the ids were always right, only what the chips SAY was missing.
    // Chips the user picked themselves already carry the same label, so this is
    // a no-op for them and never fights with an edit in progress.
    useEffect(() => {
        if (!open || !isEdit || !participantOptions.length) return;
        const nameById = new Map(participantOptions.map((o) => [String(o.cat), o.key]));
        setParticipants((prev) => {
            let changed = false;
            const next = prev.map((p) => {
                const name = nameById.get(String(p.cat));
                if (!name || name === p.key) return p;
                changed = true;
                return { ...p, key: name };
            });
            return changed ? next : prev;
        });
    }, [open, isEdit, participantOptions]);

    // Reset the form each time a fresh selection opens the popup. In recur-only
    // mode (repeating an existing booking) default the frequency to weekly. In
    // edit mode every field starts from the request being edited rather than
    // empty — the selection carries its current values.
    useEffect(() => {
        if (open) {
            setTitle(selection?.title || "");
            setParticipants(isEdit ? toChips(selection?.attendees, participantOptions) : []);
            setSchool(isEdit ? selection?.school || "" : "");
            setNeedInstructor(isEdit ? !!selection?.needInstructor : false);
            setIsEvent(isEdit ? !!selection?.isEvent : false);
            setFrequency(recurOnly ? "weekly" : "none");
            setCount(3);
            setStartH(selection?.start != null ? selection.start : 9);
            setEndH(selection?.end != null ? selection.end : 10);
            setError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, recurOnly, isEdit, selection?.requestId, selection?.roomId, selection?.date, selection?.start, selection?.end, selection?.title]);

    const timeLabel = useMemo(
        () => `${fmt12(startH)} - ${fmt12(endH)} · ${fmtDuration(startH, endH)}`,
        [startH, endH]
    );

    const repeats = frequency !== "none";

    const handleConfirm = () => {
        if (recurOnly) {
            if (!repeats) {
                setError("Pick a repeat frequency.");
                return;
            }
            onConfirm({
                recurrence: { frequency, count: Math.max(1, Math.min(52, Number(count) || 1)) },
            });
            return;
        }
        if (!title.trim()) {
            setError("Please enter a purpose for the meeting.");
            return;
        }
        if (showEduspaceFields && !school) {
            setError("Please select a school.");
            return;
        }
        if (endH <= startH) {
            setError("End time must be after the start time.");
            return;
        }
        const attendeeList = (participants || []).map((p) => ({
            id: p.cat,
            name: (p.key || "").trim(),
            value: p.cat,
            label: (p.key || "").trim(),
        }));
        onConfirm({
            meeting_title: title.trim(),
            attendees: attendeeList,
            school,
            needInstructor,
            isEvent,
            start: startH,
            end: endH,
            recurrence: repeats && !multiDate
                ? { frequency, count: Math.max(1, Math.min(52, Number(count) || 1)) }
                : null,
        });
    };

    return (
        <PopupWrapper className={open ? "active" : ""}>
            <WrapperBox
                background={globalTheme.colors.white1}
                borderRadius={20}
                maxWidth={"460px"}
                padding={"0"}
                style={{ overflow: "visible" }}
            >
                {/* Header */}
                <Flexbox column gap={4} padding={"20px 24px"} style={{ borderBottom: "1px solid #eee" }}>
                    <Text font16 inter semiBold black3>
                        {recurOnly ? "Repeat Booking" : isEdit ? "Edit Booking Request" : "New Booking"}
                    </Text>
                    {selection && (
                        <Text font12 inter medium nuetral6>
                            {selection.roomName} · {multiDate ? `${selection.dates.length} day${selection.dates.length > 1 ? "s" : ""} selected` : fmtPopupDate(selection.date)}
                        </Text>
                    )}
                    {/* Say what saving means for the request's status: an
                        approved booking edited from My Bookings goes back for
                        review, an under-review one just carries the changes. */}
                    {isEdit && (
                        <Text font11 inter regular nuetral6>
                            {selection?.wasApproved
                                ? "This booking is approved — saving changes sends it back for admin approval."
                                : "This request is still under review — your changes go to the reviewer with it."}
                        </Text>
                    )}
                    <Flexbox gap={6} alignCenter maxWidth="max-content" style={{ marginTop: 2 }}>
                        <Clock />
                        <Text font12 inter semiBold black3>
                            {timeLabel}
                        </Text>
                    </Flexbox>
                </Flexbox>

                {/* Body */}
                <Flexbox column gap={16} padding={"20px 24px"}>
                    {!recurOnly && (
                        <>
                            <Flexbox column gap={6}>
                                <Text font12 inter medium black4>
                                    Purpose of meeting <span style={{ color: globalTheme.colors.red }}>*</span>
                                </Text>
                                <input
                                    style={inputStyle}
                                    placeholder="e.g. TETR — In Class"
                                    value={title}
                                    onChange={(e) => {
                                        setTitle(e.target.value);
                                        if (error) setError("");
                                    }}
                                    autoFocus
                                />
                            </Flexbox>

                            {showEduspaceFields && (
                            <Flexbox gap={12} alignEnd>
                                <Flexbox column gap={6} style={{ flex: 1, minWidth: 0 }}>
                                    <Text font12 inter medium black4>
                                        School <span style={{ color: globalTheme.colors.red }}>*</span>
                                    </Text>
                                    {/* CustomDropdown keeps its own selection and
                                        only seeds it from `selectedOptionName`
                                        when type="school", matching by VALUE (id).
                                        Keyed per opening so a reopened popup
                                        doesn't keep the previous request's pick. */}
                                    <CustomDropdown
                                        key={`${open}-${selection?.requestId || ""}`}
                                        type="school"
                                        dropName="Select school"
                                        options={SCHOOL_OPTIONS}
                                        selectedOptionName={school || null}
                                        onSelect={(opt) => {
                                            setSchool(opt?.value || "");
                                            if (error) setError("");
                                        }}
                                    />
                                </Flexbox>
                                {/* "Is this booking related to an event?" → isEvent */}
                                <Flexbox column gap={6} maxWidth="max-content">
                                    <Text font12 inter medium black4>
                                        Event booking?
                                    </Text>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={isEvent}
                                        aria-label="Is this booking related to an event?"
                                        onClick={() => setIsEvent((v) => !v)}
                                        style={{
                                            width: 46,
                                            height: 26,
                                            flexShrink: 0,
                                            borderRadius: 999,
                                            border: "none",
                                            cursor: "pointer",
                                            padding: 3,
                                            display: "flex",
                                            justifyContent: isEvent ? "flex-end" : "flex-start",
                                            alignItems: "center",
                                            background: isEvent ? globalTheme.colors.primary : globalTheme.colors.nuetral2,
                                            transition: "background 0.15s ease",
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                background: globalTheme.colors.white1,
                                                boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                                            }}
                                        />
                                    </button>
                                </Flexbox>
                            </Flexbox>
                            )}

                            <Flexbox column gap={6}>
                                <Text font12 inter medium black4>
                                    Participants <span style={{ opacity: 0.6 }}>(optional)</span>
                                </Text>
                                <ParticipantsField>
                                    <MultiDropdown
                                        noLabel
                                        placeholder="Add participants"
                                        options={participantOptions}
                                        selectedValues={participants}
                                        onChange={(list) => setParticipants(list || [])}
                                    />
                                </ParticipantsField>
                            </Flexbox>

                            {/* <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={needInstructor}
                                    onChange={(e) => setNeedInstructor(e.target.checked)}
                                />
                                <Text font12 inter medium black4>
                                    Requires an instructor
                                </Text>
                            </label> */}

                            {/* Editable time (Google-Calendar style) */}
                            <Flexbox gap={12} alignEnd>
                                <Flexbox column gap={6}>
                                    <Text font12 inter medium black4>
                                        Start
                                    </Text>
                                    <select
                                        style={inputStyle}
                                        value={startH}
                                        onChange={(e) => {
                                            const v = Number(e.target.value);
                                            setStartH(v);
                                            if (endH <= v) setEndH(Math.min(24, v + 0.25));
                                            if (error) setError("");
                                        }}
                                    >
                                        {TIME_VALUES.filter((v) => v < 24).map((v) => (
                                            <option key={v} value={v}>
                                                {fmt12(v)}
                                            </option>
                                        ))}
                                    </select>
                                </Flexbox>
                                <Flexbox column gap={6}>
                                    <Text font12 inter medium black4>
                                        End
                                    </Text>
                                    <select
                                        style={inputStyle}
                                        value={endH}
                                        onChange={(e) => {
                                            setEndH(Number(e.target.value));
                                            if (error) setError("");
                                        }}
                                    >
                                        {TIME_VALUES.filter((v) => v > startH).map((v) => (
                                            <option key={v} value={v}>
                                                {fmt12(v)}
                                            </option>
                                        ))}
                                    </select>
                                </Flexbox>
                            </Flexbox>
                        </>
                    )}

                    {/* Recurrence — hidden for week multi-day selections (dates already chosen). */}
                    {/* {!multiDate && (
                    <Flexbox column gap={8} padding={"14px"} background={globalTheme.colors.grey8} borderRadius={12}>
                        <Text font12 inter semiBold black3>
                            Repeat
                        </Text>
                        <Flexbox gap={8} alignCenter>
                            {FREQUENCIES.filter((f) => !recurOnly || f.value !== "none").map((f) => {
                                const active = frequency === f.value;
                                return (
                                    <span
                                        key={f.value}
                                        onClick={() => setFrequency(f.value)}
                                        style={{
                                            padding: "6px 12px",
                                            borderRadius: 20,
                                            cursor: "pointer",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            border: `1px solid ${active ? globalTheme.colors.black4 : globalTheme.colors.nuetral2}`,
                                            background: active ? globalTheme.colors.black4 : globalTheme.colors.white1,
                                            color: active ? globalTheme.colors.white1 : globalTheme.colors.black4,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {f.label}
                                    </span>
                                );
                            })}
                        </Flexbox>
                        {repeats && (
                            <Flexbox gap={8} alignCenter maxWidth="max-content" style={{ marginTop: 4 }}>
                                <Text font12 inter medium black4>
                                    For
                                </Text>
                                <input
                                    type="number"
                                    min={1}
                                    max={52}
                                    value={count}
                                    onChange={(e) => setCount(e.target.value)}
                                    style={{ ...inputStyle, width: 64, padding: "6px 8px" }}
                                />
                                <Text font12 inter medium black4>
                                    {frequency === "weekly" ? "week(s)" : "day(s)"}
                                </Text>
                            </Flexbox>
                        )}
                    </Flexbox>
                    )} */}

                    {error && (
                        <Text font12 inter medium style={{ color: globalTheme.colors.red }}>
                            {error}
                        </Text>
                    )}
                </Flexbox>

                {/* Footer */}
                <Flexbox gap={10} flexEnd padding={"16px 24px"} style={{ borderTop: "1px solid #eee" }}>
                    <Button
                        buttonMd
                        name="Cancel"
                        customPadding="8px 18px"
                        onClick={loading ? undefined : onClose}
                        style={{
                            background: globalTheme.colors.white1,
                            border: `1px solid ${globalTheme.colors.nuetral2}`,
                            color: globalTheme.colors.black4,
                            opacity: loading ? 0.6 : 1,
                            pointerEvents: loading ? "none" : "auto",
                        }}
                    />
                    <Button
                        buttonMd
                        primaryFilled
                        name={loading ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save Changes" : "Confirm Booking"}
                        customPadding="8px 18px"
                        onClick={handleConfirm}
                        style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto" }}
                    />
                </Flexbox>
            </WrapperBox>
        </PopupWrapper>
    );
};

export default CreateBookingPopup;
